import { forwardRef, useId } from 'react';
import type { ReactNode, SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Select field that mirrors FormField's look (white pill, centered text, inline
 * error + a11y wiring) for the registration steps. Native <select> under the
 * hood — keyboard/screen-reader/mobile behavior comes for free; we only restyle
 * the chrome (`appearance-none` + a custom chevron).
 *
 * An empty value renders the placeholder option in the muted placeholder color,
 * matching FormField's `placeholder:` treatment.
 */
type FormSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & {
  label: string;
  visuallyHiddenLabel?: boolean;
  /** Shown as a disabled first option while no value is selected. */
  placeholder: string;
  options: readonly SelectOption[];
  error?: string | null;
  helperText?: ReactNode;
  /** Extra class added to the <select> only (label/wrapper untouched). */
  selectClassName?: string;
};

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(function FormSelect(
  { label, visuallyHiddenLabel, placeholder, options, error, helperText, selectClassName, id, value, ...selectProps },
  ref,
) {
  const reactId = useId();
  const selectId = id ?? `field-${reactId}`;
  const errorId = `${selectId}-error`;
  const helperId = helperText ? `${selectId}-helper` : undefined;
  const describedBy =
    [error ? errorId : null, helperId].filter(Boolean).join(' ') || undefined;
  const isEmpty = value === '' || value == null;

  return (
    <div className="mx-auto flex w-full max-w-[500px] flex-col gap-1">
      <label
        htmlFor={selectId}
        className={
          visuallyHiddenLabel
            ? 'sr-only'
            : "mb-1 font-['Arial',sans-serif] text-[14px] uppercase tracking-[1px] text-white/80"
        }
      >
        {label}
      </label>
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={[
            // Match FormField: white pill, centered text. appearance-none strips
            // the native arrow so our chevron is the only one shown. Right
            // padding leaves room for the chevron without offsetting the
            // centered text noticeably.
            //
            // iOS Safari ignores `text-align: center` for a <select>'s closed
            // (collapsed) value — it always renders left-aligned. The property
            // that DOES center the displayed value cross-browser (incl. iOS) is
            // `text-align-last`, so we set both: text-center handles the option
            // list / desktop, text-align-last:center fixes the iOS control.
            "h-[42px] w-full appearance-none rounded-[20px] bg-white px-9 text-center [text-align-last:center] font-['Arial',sans-serif] text-[18px]",
            isEmpty ? 'text-[#7a7a7a]' : 'text-[#1f1a26]',
            'shadow-[inset_0_2px_2px_rgba(0,0,0,0.08)]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00ffb2] focus-visible:ring-offset-2 focus-visible:ring-offset-[#3a2f44]',
            error ? 'ring-2 ring-[#ff4479]' : '',
            selectClassName ?? '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...selectProps}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-[#1f1a26]">
              {o.label}
            </option>
          ))}
        </select>
        {/* Custom chevron — pointer-events-none so clicks fall through to the
            select. Color matches the dark input text. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#1f1a26]"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </div>
      {helperText && (
        <p id={helperId} className="font-['Arial',sans-serif] text-[12px] text-white/70">
          {helperText}
        </p>
      )}
      {error && (
        <p
          id={errorId}
          role="alert"
          className="font-['Arial',sans-serif] text-[13px] leading-tight text-[#ffb3c5]"
        >
          {error}
        </p>
      )}
    </div>
  );
});
